import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "redux/newDashboard/actions.js";
import { Button, notification } from "antd";
import { getWizardStepsConfig } from "util/locale";
import CreationItem from "components/NewDashboard/CreationItem";
import IndustryCategorySelect from "components/NewDashboard/IndustryCategorySelect";
import FeedbackCaptureWizardFields from "components/NewDashboard/FeedbackCaptureWizardFields";
import { getParams, verifyDates } from "util/common";
import { apiRequest } from "util/services";
import { useHistory } from "react-router-dom";
import { Modal } from "components/General/ModalFormComponents";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";

const ZENDESK_VIRTUAL_PLATFORM = 78;
const FEEDBACK_CAPTURE_VIRTUAL_PLATFORM = 79;
// Mobile App Reviews dashboards get sentiment and intent only: no topic library is offered.
const MOBILE_APP_REVIEWS_PLATFORM = 11;
// Mirrors the API: Mobile App Reviews, ETS (33–36), Trustpilot (40) and Feedback Capture
// need no topic library.
const TOPIC_LIBRARY_EXEMPT_PLATFORMS = [
  MOBILE_APP_REVIEWS_PLATFORM,
  33,
  34,
  35,
  36,
  40,
  FEEDBACK_CAPTURE_VIRTUAL_PLATFORM,
];

const requiresTopicLibrary = (platform, platforms = []) =>
  !TOPIC_LIBRARY_EXEMPT_PLATFORMS.includes(platform) &&
  !(platform === 12 && platforms.includes("Trustpilot"));

const buildFeedbackSelections = (localState) => {
  const ids = localState.feedbackInstrumentIds || [];
  const selections = localState.feedbackQuestionSelections || {};
  return ids
    .map((id) => ({
      instrument_id: id,
      question_ids: selections[id] || [],
    }))
    .filter((item) => item.question_ids.length > 0);
};

const countFeedbackQuestions = (localState) =>
  buildFeedbackSelections(localState).reduce(
    (sum, item) => sum + item.question_ids.length,
    0
  );

const resolveZendeskTargetPlatform = (zendeskSystem) => {
  const value = (zendeskSystem || "").toLowerCase();
  if (value.includes("live")) return 8;
  return 7;
};

const getAllowedZendeskSystems = (platforms = []) => {
  const options = [];
  if (platforms.some((p) => p.id === 7 && p.allowed)) {
    options.push("Zendesk Tickets");
  }
  if (platforms.some((p) => p.id === 8 && p.allowed)) {
    options.push("Zendesk Live Chat");
  }
  return options.length ? options : ["Zendesk Tickets", "Zendesk Live Chat"];
};

const getCreationStepCount = (stepsConfig, platform, creationType) => {
  if (platform === ZENDESK_VIRTUAL_PLATFORM) {
    const ticketsSteps = Object.keys(stepsConfig[7][creationType]).length;
    const liveChatSteps = Object.keys(stepsConfig[8][creationType]).length;
    return 1 + Math.max(ticketsSteps, liveChatSteps);
  }
  return Object.keys(stepsConfig[platform][creationType]).length;
};

const getCreationStepFields = (
  stepsConfig,
  platform,
  creationType,
  step,
  zendeskSystem
) => {
  if (platform !== ZENDESK_VIRTUAL_PLATFORM) {
    return stepsConfig[platform][creationType][`step${step}`];
  }
  if (step === 1) {
    return stepsConfig[ZENDESK_VIRTUAL_PLATFORM][creationType].step1;
  }
  const targetPlatform = resolveZendeskTargetPlatform(zendeskSystem);
  return stepsConfig[targetPlatform][creationType][`step${step - 1}`];
};

const isSocialMediaStep3FieldVisible = (fieldKey, platforms = []) => {
  if (fieldKey === "hashtags") {
    return platforms.indexOf("Instagram Hashtag Search") !== -1;
  }
  if (fieldKey === "link") {
    return platforms.indexOf("Trustpilot") !== -1;
  }
  return true;
};

const StepsWrapper = ({
  user,
  platforms = [],
  wizardForm,
  setWizardForm,
  advancedAllowed,
  trial,
  facebookPageObjects = [],
  facebookAdObjects = [],
  instagramAccounts = [],
  forums = [],
  linkedinPages = [],
  facebookPageObjectsLoading,
  facebookAdObjectsLoading,
  instagramAccountsLoading,
  forumsLoading,
  linkedPagesLoading,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const localState = wizardForm;
  const setState = setWizardForm;
  const [columnNames, setColumnNames] = useState();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [missingDateModalOpen, setMissingDateModalOpen] = useState(false);
  const [showDateWarning, setShowDateWarning] = useState(false);

  const newDashboardReducer = useSelector((state) => state.newDashboardReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const step = newDashboardReducer.currentStep;
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setState((prev) => ({ ...prev, product_links: [], category_links: [] }));
  }, [localState.ecommerceCreationLinkType]);

  if (!step) return <div />;

  const stepsConfig = getWizardStepsConfig(i18n.language);
  const { platform, creationType } = newDashboardReducer;
  const totalSteps = getCreationStepCount(stepsConfig, platform, creationType);
  let info = getCreationStepFields(
    stepsConfig,
    platform,
    creationType,
    step,
    localState.zendesk_system
  );

  if (platform === ZENDESK_VIRTUAL_PLATFORM && step === 1) {
    const allowedZendeskSystems = getAllowedZendeskSystems(platforms);
    info = info.map((field) =>
      field.key === "zendesk_system"
        ? {
            ...field,
            acceptedValues: allowedZendeskSystems,
            defaultValue: allowedZendeskSystems[0],
          }
        : field
    );
  } else if (platform === ZENDESK_VIRTUAL_PLATFORM && step > 1) {
    info = (info || []).filter((field) => field.key !== "dashboard_name");
  }

  if (newDashboardReducer.platform === 9 && step === 2) {
    info[0].acceptedValues = facebookPageObjects.map(({ id, name }) => ({
      value: id,
      text: name,
    }));
  } else if (newDashboardReducer.platform === 10 && step === 2) {
    info[0].acceptedValues = facebookAdObjects.map(({ account_id }) => ({
      value: account_id,
      text: account_id,
    }));
  }

  const isFeedbackInstrumentStep = info?.some(
    (item) => item.valueType === "feedbackInstrument"
  );
  const feedbackInstrumentStepBlocked =
    isFeedbackInstrumentStep &&
    localState.feedbackInstrumentsLoaded &&
    !localState.feedbackInstrumentsAvailable;

  const creationChecker = () => {
    const {
      num_topics,
      num_iters,
      app_url,
      beta,
      customerDataFileS3Link,
      customerFeedbackTextIndex,
      start_date,
      end_date,
      geolocation: _geolocation,
      city: _city,
    } = localState;
    const { platform, creationType, industry_id } = newDashboardReducer;
    if (
      requiresTopicLibrary(platform, localState.platforms) &&
      (industry_id == null || industry_id === -1)
    ) {
      notification.error({
        message: t("new_dashboard.topicLibraryRequired", {
          defaultValue: "Please select a topic library for this dashboard.",
        }),
      });
      return false;
    }
    if (
      num_topics !== undefined &&
      num_topics !== null &&
      num_topics !== "" &&
      (num_topics < 10 || num_topics > 100)
    ) {
      notification.error({
        message: t("notifications.9"),
      });
      return false;
    }
    if (num_iters < 20 || num_iters > 1000) {
      notification.error({
        message: t("notifications.10"),
      });
      return false;
    }
    if ((platform === 2 || platform === 4) && !app_url) {
      notification.error({
        message: t("notifications.11"),
      });
      return false;
    }
    if (beta && beta < 0) {
      notification.error({
        message: t("notifications.12"),
      });
      return false;
    }
    if (creationType === 3 && !customerDataFileS3Link) {
      notification.error({ message: t("notifications.13") });
      return false;
    }
    if (creationType === 3 && !customerFeedbackTextIndex) {
      notification.error({
        message: t("notifications.14"),
      });
      return false;
    }
    let dateVerification = verifyDates(start_date, end_date);

    if (!dateVerification.check) {
      notification.error({
        message: dateVerification.msg,
      });
      return false;
    }
    return true;
  };

  const createDashboard = async () => {
    const {
      app_url,
      google_play_app_url,
      app_store_app_url,
      ecommerceCreationLinkType,
    } = localState;
    const { platform, creationType, industry_id } = newDashboardReducer;
    if (!creationChecker()) return;

    setLoading(true);

    if (platform === FEEDBACK_CAPTURE_VIRTUAL_PLATFORM) {
      const selections = buildFeedbackSelections(localState);
      if (!localState.dashboard_name || !localState.feedbackInstrumentIds?.length) {
        notification.info({ message: t("notifications.18") });
        setLoading(false);
        return;
      }
      if (!selections.length) {
        notification.info({
          message: t("new_dashboard.feedbackCapture.select_questions"),
        });
        setLoading(false);
        return;
      }
      const result = await apiRequest(
        "feedback/create-dashboard",
        "POST",
        {
          dashboard_name: localState.dashboard_name,
          selections,
          model_id: industry_id,
          industry_id,
        },
        true
      );
      if (!result.error) {
        notification.success({ message: t("notifications.17") });
        setState({});
        dispatch({
          type: actions.UPDATE_NEW_Dashboard,
          payload: { currentStep: -1 },
        });
        history.push("/console/myDashboards");
      } else {
        notification.error({ message: result.error });
      }
      setLoading(false);
      return;
    }

    let payload = {
      ...localState,
      platform_id: platform,
      user_id: user.uid,
      isCategory: ecommerceCreationLinkType === "product_links" ? false : true,
      model_id: platform === MOBILE_APP_REVIEWS_PLATFORM ? -1 : industry_id,
    };
    if (platform === ZENDESK_VIRTUAL_PLATFORM) {
      payload.platform_id = resolveZendeskTargetPlatform(
        localState.zendesk_system
      );
      delete payload.zendesk_system;
    }
    let api = "dashboards/create/advanced";
    if (creationType === "simple") {
      api = "dashboards/create/simple";
      delete payload.num_iters;
    }
    if (platform === 2) {
      const playstore_id = getParams(app_url).id;
      if (!playstore_id) {
        notification.error({
          message: t("notifications.15"),
        });
        setLoading(false);
        return;
      }
      delete payload.app_url;
      payload.playstore_id = playstore_id;
    } else if (platform === 4) {
      const splittedURL = app_url ? app_url.split("/") : "";
      const app_name =
        splittedURL && splittedURL.length >= 6 ? splittedURL[5] : "";
      if (!app_name) {
        notification.error({
          message: t("notifications.16"),
        });
        setLoading(false);
        return;
      }
      delete payload.app_url;
      payload.app_name = app_name;
    } else if (platform === 11) {
      const play_store_id = getParams(google_play_app_url).id;
      if (
        (localState.platforms || []).indexOf("Google Play") !== -1 &&
        !play_store_id
      ) {
        notification.error({
          message: t("notifications.15"),
        });
        setLoading(false);
        return;
      }
      delete payload.google_play_app_url;
      payload.play_store_id = play_store_id;

      const splittedURL = app_store_app_url ? app_store_app_url.split("/") : "";
      const app_store_app_name =
        splittedURL && splittedURL.length >= 6 ? splittedURL[5] : "";
      const chunk =
        splittedURL && splittedURL.length >= 6 ? splittedURL[6] : "";
      const app_store_app_id =
        chunk && chunk.length >= 3 ? chunk.split("?")[0] : "";

      if (
        (localState.platforms || []).indexOf("App Store") !== -1 &&
        !app_store_app_name
      ) {
        notification.error({
          message: t("notifications.16"),
        });
        setLoading(false);
        return;
      }
      if (
        (localState.platforms || []).indexOf("App Store") !== -1 &&
        !app_store_app_id
      ) {
        notification.error({
          message: t("notifications.16"),
        });
        setLoading(false);
        return;
      }
      delete payload.app_store_app_url;
      payload.app_store_app_name = app_store_app_name;
      payload.app_store_app_id = app_store_app_id;
    }

    for (let key in payload) {
      if (!payload[key]) delete payload[key];
    }

    if (payload["geolocation"] === "true") {
      payload["geolocation"] = true;
    } else {
      payload["geolocation"] = false;
    }

    if (payload["language"] !== "Turkish") {
      delete payload["city"];
      delete payload["district"];
      payload["geolocation"] = false;
    }
    if (payload["npsScale"]) {
      const scaleWithText = payload["npsScale"];
      const scale = scaleWithText
        .split(" ")
        .slice(0, 3)
        .join(" ");
      payload["npsScale"] = scale;
    }
    const result = await apiRequest(api, "POST", payload, true);
    if (!result.error) {
      notification.success({ message: t("notifications.17") });
      setState({});
      dispatch({
        type: actions.UPDATE_NEW_Dashboard,
        payload: {
          currentStep: -1,
        },
      });
      //history.push("/welcome");
      history.push("/console/myDashboards");
    } else {
      notification.error({
        message: result.error,
      });
    }
    setLoading(false);
  };

  const updateStep = (increment, warning = true) => {
    const { creationType } = newDashboardReducer;

    try {
      if (step === 1 && increment !== -1) {
        let StepInputs =
          stepsConfig[newDashboardReducer.platform][creationType][`step${step}`];
        let flag = false;
        if (StepInputs.length) {
          StepInputs.forEach((stepInfo) => {
            if (
              stepInfo["key"] === "start_date" ||
              stepInfo["key"] === "end_date"
            ) {
              flag = true;
            }
          });
        }
        if (flag) {
          if (
            localState["start_date"] === undefined ||
            localState["end_date"] === undefined
          ) {
            notification.info({
              message: t("warning.1"),
              icon: (
                <InfoCircleOutlined
                  style={{ color: theme === "dark" ? "white" : "black" }}
                />
              ),
            });
          } else if (
            verifyDates(localState["start_date"], localState["end_date"])
          ) {
            let diff = dayjs(localState["end_date"]).diff(
              localState["start_date"],
              "days"
            );
            if (diff < 30) {
              if (warning) {
                setShowDateWarning(true);
                return;
              }
            }
          }
        }
      }
    } catch (err) {
      console.error("Something Went wrong");
    }

    if (increment === 1) {
      const { platform: activePlatform } = newDashboardReducer;
      for (let i = 0; i < info.length; i++) {
        if (
          activePlatform === 12 &&
          step === 3 &&
          !isSocialMediaStep3FieldVisible(
            info[i].key,
            localState.platforms || []
          )
        ) {
          continue;
        }
        if (info[i].required && !localState[info[i].key]) {
          if (
            info[i].key === "feedbackQuestionSelections" &&
            countFeedbackQuestions(localState) > 0
          ) {
            continue;
          }
          if (
            info[i].key === "feedbackInstrumentIds" &&
            (localState.feedbackInstrumentIds || []).length
          ) {
            continue;
          }
          notification.info({
            message: t("notifications.18"),
            icon: (
              <InfoCircleOutlined
                style={{ color: theme === "dark" ? "white" : "black" }}
              />
            ),
          });
          return;
        }
        if (info[i].title === "Number Of Topics") {
          const { num_topics } = localState;
          if (
            num_topics !== undefined &&
            num_topics !== null &&
            num_topics !== "" &&
            (num_topics < 10 || num_topics > 100)
          ) {
            notification.error({
              message: t("notifications.9"),
            });
            return;
          }
        }
        if (info[i].title === "Platforms") {
          const { platforms } = localState;
          if (platforms.length === 0) {
            notification.error({
              message: t("notifications.19"),
            });
            return;
          }
        }
      }
    }
    if (step === totalSteps && increment === 1) {
      setShowConfirmationModal(true);
    } else {
      dispatch({
        type: actions.UPDATE_NEW_Dashboard,
        payload: {
          currentStep: newDashboardReducer.currentStep + increment,
        },
      });
    }
  };

  return (
    <>
      <div className={styles.container} id="datasource_section">
        <div className={styles.header}>
          <h5>
            {t("new_dashboard.11")} {step}/{totalSteps}
          </h5>
        </div>
        <div className={styles.dynamicContent}>
          {info?.map((item) => {
            if (
              newDashboardReducer.platform === 11 &&
              step === 2 &&
              (((localState.platforms || []).indexOf("App Store") === -1 &&
                item.key === "app_store_app_url") ||
                ((localState.platforms || []).indexOf("Google Play") === -1 &&
                  item.key === "google_play_app_url"))
            ) {
              return null;
            }
            if (
              newDashboardReducer.platform === 12 &&
              step === 3 &&
              !isSocialMediaStep3FieldVisible(item.key, localState.platforms || [])
            ) {
              return null;
            }

            if (
              item.valueType === "feedbackInstrument" ||
              item.valueType === "feedbackQuestions"
            ) {
              return (
                <FeedbackCaptureWizardFields
                  key={item.key}
                  field={item}
                  localState={localState}
                  setState={setState}
                />
              );
            }

            return (
              <React.Fragment key={item.key}>
                <CreationItem
                  item={item}
                  setState={setState}
                  localState={localState}
                  columnNames={columnNames}
                  setColumnNames={setColumnNames}
                  defaultValue={localState[item.key]}
                  user={user}
                />
                {step === 1 &&
                  item.key === "dashboard_name" &&
                  newDashboardReducer.platform !==
                    MOBILE_APP_REVIEWS_PLATFORM && <IndustryCategorySelect />}
              </React.Fragment>
            );
          })}
          {step === 1 &&
            newDashboardReducer.platform !== MOBILE_APP_REVIEWS_PLATFORM &&
            !(info || []).some((field) => field.key === "dashboard_name") && (
              <IndustryCategorySelect />
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("Twitter") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.16"),
                    subtitle: t("new_dashboard.53"),
                    valueType: "multiInput",
                    key: "any_keywords",
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["any_keywords"]}
                  user={user}
                />
                <CreationItem
                  item={{
                    title: t("new_dashboard.17"),
                    subtitle: t("new_dashboard.54"),
                    valueType: "multiInput",
                    key: "all_keywords",
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["all_keywords"]}
                  user={user}
                />
                <CreationItem
                  item={{
                    title: t("new_dashboard.18"),
                    subtitle: t("new_dashboard.19"),
                    valueType: "multiInput",
                    key: "excluded_keywords",
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["excluded_keywords"]}
                  user={user}
                />
                <CreationItem
                  item={{
                    title: t("new_dashboard.20"),
                    subtitle: t("new_dashboard.55"),
                    valueType: "multiInput",
                    key: "banned_accounts",
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["banned_accounts"]}
                  user={user}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("Reddit") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.21"),
                    subtitle: t("new_dashboard.22"),
                    valueType: "input",
                    placeholder: t("new_dashboard.23"),
                    key: "reddit_search_term",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["reddit_search_term"]}
                  user={user}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("Ekşi Sözlük") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.24"),
                    subtitle: t("new_dashboard.25"),
                    valueType: "input",
                    placeholder: t("new_dashboard.23"),
                    key: "eksisozluk_search_term",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["eksisozluk_search_term"]}
                  user={user}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf(
              "Facebook and Instagram ads"
            ) !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.26"),
                    subtitle: t("new_dashboard.56"),
                    valueType: "dropdown_2",
                    acceptedValues: facebookAdObjects.map(({ account_id }) => ({
                      value: account_id,
                      text: account_id,
                    })),
                    placeholder: t("new_dashboard.57"),
                    key: "ad_account_id",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["ad_account_id"]}
                  user={user}
                  disabled={facebookAdObjectsLoading}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("TikTok") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: "TikTok search Term",
                    subtitle: "Add TikTok search term",
                    valueType: "input",
                    placeholder: "",
                    key: "tiktok_search_term",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["tiktok_search_term"]}
                  user={user}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("Facebook Page") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.27"),
                    subtitle: t("new_dashboard.58"),
                    valueType: "dropdown_2",
                    acceptedValues: facebookPageObjects.map(({ id, name }) => ({
                      value: id,
                      text: name,
                    })),
                    placeholder: t("new_dashboard.59"),
                    key: "page_id",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["page_id"]}
                  user={user}
                  disabled={facebookPageObjectsLoading}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("Instagram Account Posts") !==
              -1 && (
              <>
                <CreationItem
                  item={{
                    title: "Instagram accounts",
                    subtitle: t("new_dashboard.58"),
                    valueType: "dropdown_2",
                    acceptedValues: instagramAccounts.map(({ id, name }) => ({
                      value: id,
                      text: name,
                    })),
                    placeholder: "Select An Instagram Account",
                    key: "instagram_account_id",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["page_id"]}
                  user={user}
                  disabled={instagramAccountsLoading}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("Forums/Community") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: "Select Forum",
                    subtitle: "Choose Forum link to collect forum conversation",
                    valueType: "dropdown_2",
                    acceptedValues: forums.map(({ id, name }) => ({
                      value: id,
                      text: name,
                    })),
                    key: "forum_id",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["forum_id"]}
                  user={user}
                  disabled={forumsLoading}
                />
              </>
            )}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("LinkedIn Posts") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: "Select Linkedin page",
                    subtitle: "Choose a page to get posts",
                    valueType: "dropdown_2",
                    acceptedValues: linkedinPages.map(
                      ({ org_id, org_name }) => ({
                        value: org_id,
                        text: org_name,
                      })
                    ),
                    key: "linkedin_org_id",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["linkedin_org_id"]}
                  user={user}
                  disabled={linkedPagesLoading}
                />
              </>
            )}
          {/* {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("doktortakvimi") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: "Doktor Takvimi Cities",
                    subtitle: "Select cities for Doktor Takvimi platform",
                    valueType: "dropdownMulti",
                    acceptedValues: turkishCities,
                    placeholder: "Select cities (max 5)",
                    key: "doktortakvimi_cities",
                    required: true,
                    maxSelection: 5,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["doktortakvimi_cities"]}
                  user={user}
                />
              </>
            )} */}
          {newDashboardReducer.platform === 12 &&
            step === 3 &&
            (localState.platforms || []).indexOf("Şikayet Var") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.28"),
                    subtitle: t("new_dashboard.29"),
                    valueType: "input",
                    placeholder: `${t(
                      "new_dashboard.30"
                    )}: https://www.sikayetvar.com/thy`,
                    key: "brand",
                    required: true,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["brand"]}
                  user={user}
                />
              </>
            )}
          {/* {newDashboardReducer.platform === 30 &&
            step === 3 &&
            (localState.platforms || []).indexOf("doktortakvimi") !== -1 && (
              <>
                <CreationItem
                  item={{
                    title: "Doktor Takvimi Cities",
                    subtitle: "Select cities for Doktor Takvimi platform",
                    valueType: "dropdownMulti",
                    acceptedValues: turkishCities,
                    placeholder: "Select cities (max 5)",
                    key: "doktortakvimi_cities",
                    required: true,
                    maxSelection: 5,
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["doktortakvimi_cities"]}
                  user={user}
                />
              </>
            )} */}
          {!trial && newDashboardReducer.platform === 1 && step === 1 && (
            <>
              <CreationItem
                item={{
                  title: t("new_dashboard.60"),
                  subtitle: t("new_dashboard.31"),
                  valueType: "date",
                  key: "start_date",
                }}
                setState={setState}
                localState={localState}
                defaultValue={localState["start_date"]}
                user={user}
              />
              <CreationItem
                item={{
                  title: t("new_dashboard.61"),
                  subtitle: t("new_dashboard.32"),
                  valueType: "date",
                  key: "end_date",
                }}
                setState={setState}
                localState={localState}
                defaultValue={localState["end_date"]}
                user={user}
              />
            </>
          )}

          {newDashboardReducer.platform === 3 &&
            localState["customerRatingIndex"] &&
            (newDashboardReducer.creationType === "advanced"
              ? step === 8
              : step === 7) && (
              <CreationItem
                item={{
                  title: t("new_dashboard.33"),
                  valueType: "radio",
                  acceptedValues: [
                    { text: "No", key: "false" },
                    { text: "Yes", key: "true" },
                  ],
                  key: "nps",
                }}
                setState={setState}
                localState={localState}
                defaultValue={localState["nps"]}
                user={user}
              />
            )}
          {newDashboardReducer.platform === 3 &&
            localState["customerRatingIndex"] &&
            (newDashboardReducer.creationType === "advanced"
              ? step === 8
              : step === 7) &&
            localState["nps"] === "true" && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.65"),
                    subtitle: t("new_dashboard.66"),
                    valueType: "dropdown",
                    acceptedValues: ["1 to 5 (VoC Score)", "0 to 10 (NPS)"],
                    key: "npsScale",
                    defaultValue: "0 to 10 (NPS)",
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={
                    localState["npsScale"]
                      ? localState["npsScale"]
                      : "0 to 10 (NPS)"
                  }
                  user={user}
                />
              </>
            )}
          {newDashboardReducer.platform === 3 &&
            localState["customerDateIndex"] &&
            (newDashboardReducer.creationType === "advanced"
              ? step === 7
              : step === 6) && (
              <CreationItem
                item={{
                  title: t("new_dashboard.35"),
                  subtitle: t("new_dashboard.36"),
                  valueType: "date",
                  key: "lastDate",
                }}
                setState={setState}
                localState={localState}
                defaultValue={localState["lastDate"]}
                user={user}
              />
            )}
          {/* {newDashboardReducer.platform !== 13 && newDashboardReducer.platform !== 11 && localState["language"] === "Turkish" &&
            step === 1 && (
              <>
                <CreationItem
                  item={{
                    title: t("new_dashboard.37"),
                    subtitle: t("new_dashboard.38"),
                    valueType: "radio",
                    acceptedValues: [
                      { text: "No", key: "false" },
                      { text: "Yes", key: "true" },
                    ],
                    key: "geolocation",
                  }}
                  setState={setState}
                  localState={localState}
                  defaultValue={localState["geolocation"]}
                  user={user}
                />
                {localState["geolocation"] === "true" && (
                  <>
                    <CreationItem
                      item={{
                        title: t("new_dashboard.39"),
                        subtitle: t("new_dashboard.40"),
                        valueType: "dropdown",
                        acceptedValues: citiesTurkey,
                        placeholder: t("new_dashboard.41"),
                        key: "city",
                        required: false,
                      }}
                      setState={setState}
                      localState={localState}
                      defaultValue={localState["city"]}
                      user={user}
                    />
                    {localState["city"] && (
                      <CreationItem
                        item={{
                          title: t("new_dashboard.42"),
                          subtitle: t("new_dashboard.43"),
                          valueType: "dropdown",
                          acceptedValues: districtsTurkey[localState["city"]],
                          placeholder: t("new_dashboard.44"),
                          key: "district",
                        }}
                        setState={setState}
                        localState={localState}
                        defaultValue={localState["district"]}
                        user={user}
                      />
                    )}
                  </>
                )}
              </>
            )} */}
        </div>
        <div className={styles.btnsContainer}>
          <div className={styles.backBtn}>
            <Button loading={loading} onClick={() => updateStep(-1)}>
              {t("new_dashboard.50")}
            </Button>
          </div>
          <div className={styles.nextBtns}>
            <div className={styles.btn}>
              <Button
                disabled={
                  (info?.[0].disableIfNo &&
                    localState[info?.[0].key] === "false") ||
                  feedbackInstrumentStepBlocked
                }
                loading={loading}
                onClick={() => updateStep(1)}
                title={
                  feedbackInstrumentStepBlocked
                    ? t("new_dashboard.feedbackCapture.no_instruments_hint")
                    : undefined
                }
              >
                {step === totalSteps
                  ? t("new_dashboard.48")
                  : t("new_dashboard.52")}
              </Button>
            </div>
            {info?.[0].skipAll && (
              <div className={styles.skipBtn}>
                <Button
                  loading={loading}
                  onClick={() => setShowConfirmationModal(true)}
                >
                  {t("new_dashboard.51")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        title={"Are you sure?"}
        showModal={showDateWarning}
        onClose={() => setShowDateWarning(false)}
        onSave={() => {
          updateStep(1, false);
          setShowDateWarning(false);
        }}
        showSaveButton={true}
        showCancelButton={true}
        textonButton={t("new_dashboard.10")}
        titleAsset=""
        widthofmodal="430px"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <p
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              margin: "0px",
              paddingBottom: "10px",
              color: theme === "dark" ? "white" : "black",
            }}
          >
            Are you sure about the date range you specified?
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              columnGap: "5px",
              padding: "10px",
              textAlign: "left",
              color:
                theme === "dark"
                  ? "color: rgba(255, 255, 255, 0.6)"
                  : "color: rgba(255, 255, 255, 0.6)",
              background: "rgba(111, 108, 153, 0.05)",
            }}
          >
            {theme === "dark" ? (
              <img className={styles.icon} src="/assets/info1.svg" alt="" />
            ) : (
              <img
                className={styles.icon}
                src="/assets/info1_black.svg"
                alt=""
              />
            )}
            Broadened date range allows to access more data you can then narrow
            the results by filtering
          </div>
        </div>
      </Modal>
      <Modal
        title={t("new_dashboard.45")}
        showModal={missingDateModalOpen}
        onClose={() => setMissingDateModalOpen(false)}
        onSave={createDashboard}
        showSaveButton={true}
        textonButton={t("new_dashboard.10")}
        titleAsset=""
        widthofmodal="530px"
      >
        <p style={{ color: theme === "dark" ? "white" : "black" }}>
          {t("new_dashboard.46")}
        </p>
      </Modal>
      <Modal
        title={t("new_dashboard.47")}
        showModal={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onSave={() => createDashboard()}
        showSaveButton={true}
        hideCloseIcon={true}
        showCancelButton={true}
        textonButton={t("new_dashboard.48")}
        titleAsset=""
        description={t("new_dashboard.62")}
        loadingSaveClick={loading}
        widthofmodal={window.innerWidth < 800 ? "350px" : "650px"}
      >
        <p
          style={{
            color: theme === "dark" ? "white" : "black",
            fontSize: "14px",
          }}
        >
          {t("new_dashboard.63")}
        </p>
      </Modal>
    </>
  );
};

export default StepsWrapper;
